import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable property picker.
 *
 * The Client Service object carries 313 properties and Deals over 600. A plain
 * select makes finding one a scrolling exercise, and the names that matter
 * (effective_date_, retained_or_churned) are impossible to spot by eye. This
 * searches label AND internal name, because a report definition is written
 * against the internal name and that is often what the reader knows.
 *
 * Properties are grouped by their HubSpot property group, so related fields
 * stay together rather than being flattened into one long list.
 */
export default function PropertyPicker({
  properties = [],
  value,
  onChange,
  placeholder = "Choose a property…",
  multiple = false,
  disabled = false,
  allowClear = true,
  testId = "property-picker",
}) {
  const [open, setOpen] = useState(false);

  const selected = multiple ? value || [] : value ? [value] : [];
  const byName = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.name, p])),
    [properties],
  );

  // Group, then sort groups by name with ungrouped last.
  const groups = useMemo(() => {
    const buckets = new Map();
    for (const property of properties) {
      const label = property.group_label || property.group_name || "Other";
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label).push(property);
    }
    return [...buckets.entries()]
      .map(([label, items]) => [
        label,
        items.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name)),
      ])
      .sort(([a], [b]) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)));
  }, [properties]);

  const pick = (name) => {
    if (!multiple) {
      onChange(name === value && allowClear ? "" : name);
      setOpen(false);
      return;
    }
    const next = selected.includes(name)
      ? selected.filter((n) => n !== name)
      : [...selected, name];
    onChange(next);
  };

  const triggerLabel = () => {
    if (!selected.length) return placeholder;
    if (!multiple) return byName[selected[0]]?.label || selected[0];
    if (selected.length === 1) return byName[selected[0]]?.label || selected[0];
    return `${selected.length} properties`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`flex w-full items-center justify-between gap-2 rounded border border-[#cccccc] bg-white px-2.5 py-1.5 text-left text-sm transition-colors hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none disabled:cursor-not-allowed disabled:bg-[#f5f5f5] ${
            selected.length ? "text-[#323c44]" : "text-[#7d8b96]"
          }`}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">{triggerLabel()}</span>
          <span className="flex flex-shrink-0 items-center gap-1">
            {allowClear && selected.length > 0 && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear"
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(multiple ? [] : "");
                }}
                className="rounded p-0.5 text-[#a9b3bb] hover:bg-slate-100 hover:text-[#4a5b68]"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 text-[#a9b3bb]" />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[380px] p-0">
        <Command
          // Search label and internal name together: definitions are written
          // against the internal name, and that is often what is known.
          filter={(itemValue, search) => {
            const property = byName[itemValue];
            const haystack = `${itemValue} ${property?.label || ""}`.toLowerCase();
            return haystack.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search properties…" className="text-sm" />
          <CommandList className="max-h-[320px]">
            <CommandEmpty className="py-6 text-center text-sm text-[#7d8b96]">
              No property matches that.
            </CommandEmpty>
            {groups.map(([groupLabel, items]) => (
              <CommandGroup key={groupLabel} heading={groupLabel}>
                {items.map((property) => (
                  <CommandItem
                    key={property.name}
                    value={property.name}
                    onSelect={() => pick(property.name)}
                    className="flex items-start gap-2"
                  >
                    <Check
                      className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${
                        selected.includes(property.name) ? "text-[#119996]" : "text-transparent"
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-[#323c44]">
                        {property.label || property.name}
                      </span>
                      {/* The internal name is what the definition stores, so
                          it is shown rather than hidden behind the label. */}
                      <span className="block truncate font-mono text-[10px] text-[#a9b3bb]">
                        {property.name}
                        {property.type ? ` · ${property.type}` : ""}
                      </span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
